import React, { useEffect, useState } from 'react';
import { Box, Grid, makeStyles, Typography } from '@material-ui/core';
import PropTypes from 'prop-types';

import { NumberIndicator, SpeciesTypeFilter } from 'src/scenes/Project/components';
import { ChartWrapper, FilterYearComparison } from 'src/components';
import { buildSpeciesIndicator, buildSpeciesChart } from 'src/scenes/Project/scenes/Dashboard/componentsHelper';
import clsx from 'clsx';


const useStyles = makeStyles(theme => ({
  itemRootBorder: {
    borderRight: `1px solid ${theme.palette.common.gray}`,
  },
  indicatorCell: {
    flex: '1 1 0',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  subtitle: {
    fontWeight: 500,
    textTransform: 'uppercase',
    color: theme.palette.common.gray600,
    lineHeight: 1,
  },
  group: {
    border: `1px solid ${theme.palette.common.gray}`,
    borderRadius: theme.shape.borderRadius,
  },
  groupActive: {
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius,
  },
}));

const getRatio = (value, total) => {
  if (total === 0) {
    return 0;
  }
  if (value === undefined || total === undefined) {
    return null;
  }
  return parseInt(((value - total) / total) * 100, 10);
};

const SpeciesByYear = ({ speciesByYear, setSpeciesByYear }) => {
  const classes = useStyles();

  const { speciesIndicators, speciesChart } = speciesByYear;

  const [ speciesTypeFilter, setSpeciesTypeFilter ] = useState('total');
  const [ speciesByYearTypeFilter, setSpeciesByYearTypeFilter ] = useState('total');

  const [ yearComparison, setYearComparison ] = useState([]);
  const [ yearComparisonOptions, setYearComparisonOptions ] = useState([]);

  useEffect(() => {
    const { originData: chartData } = speciesChart;
    if (chartData) {
      const speciesChart = buildSpeciesChart({ chartData, prefix: speciesByYearTypeFilter });
      setSpeciesByYear(s => ({ ...s, speciesChart }));
    }
    //eslint-disable-next-line
  }, [ speciesByYearTypeFilter ]);

  useEffect(() => {
    const { originData: indicatorsData } = speciesIndicators;
    if (indicatorsData) {
      const speciesIndicators = buildSpeciesIndicator({ indicatorsData, prefix: speciesTypeFilter });
      setYearComparisonOptions(speciesIndicators.years);
      const lastYear = speciesIndicators.years[speciesIndicators.years.length - 1];
      const firstYear = speciesIndicators.years[0];
      setYearComparison([ lastYear, firstYear ]);
      setSpeciesByYear(s => ({ ...s, speciesIndicators }));
    }

    //eslint-disable-next-line
  }, [ speciesTypeFilter ]);


  const getValue = type => speciesIndicators[type]?.data[yearComparison[0]]?.value;
  const getComparisonValue = type => speciesIndicators[type]?.data[yearComparison[1]]?.value;

  return (
    <>
      <Box my={3} mb={1} >
        <Box my={0.5} display="flex" alignItems="center">
          <Typography className={classes.subtitle} variant="body2" component="span">
            Especies
            <Typography variant="body2" display="inline" color="primary" component="span">
              <strong> {yearComparison[0]}</strong>
            </Typography>
            &nbsp;con respecto a
            <Typography variant="body2" display="inline" color="primary" component="span">
              <strong> {yearComparison[1]}</strong>
            </Typography>
          </Typography>
          <Box flexGrow={1} />
          <Box className={classes.group}>
            <FilterYearComparison
              id="species-year-comparison"
              label="Rango de años"
              prevValue={yearComparison}
              options={yearComparisonOptions}
              onChange={setYearComparison}
            />
          </Box>

          <Box display="flex" className={classes.group} p={0.5} ml={1}>
            <SpeciesTypeFilter speciesType={speciesTypeFilter} setSpeciesType={setSpeciesTypeFilter} />
          </Box>
        </Box>
        <Box className={classes.group} p={1}>
          <Grid container spacing={2}>
            <Grid item className={clsx(classes.itemRootBorder, classes.indicatorCell)}>
              <NumberIndicator
                title="Ocurrencias"
                value={getValue('occurrences')}
                total={getComparisonValue('occurrences')}
                rate={getRatio(getValue('occurrences'), getComparisonValue('occurrences'))}
                size="small"
                comparisonLabel={yearComparison[1]?.toString()}
              />
            </Grid>
            <Grid item className={clsx(classes.itemRootBorder, classes.indicatorCell)}>
              <NumberIndicator
                title="Nativo"
                value={getValue('natives')}
                total={getComparisonValue('natives')}
                rate={getRatio(getValue('natives'), getComparisonValue('natives'))}
                size="small"
                comparisonLabel={yearComparison[1]?.toString()}
              />
            </Grid>
            <Grid item className={clsx(classes.itemRootBorder, classes.indicatorCell)}>
              <NumberIndicator
                title="Endémica"
                value={getValue('endemic')}
                total={getComparisonValue('endemic')}
                rate={getRatio(getValue('endemic'), getComparisonValue('endemic'))}
                size="small"
                comparisonLabel={yearComparison[1]?.toString()}
              />
            </Grid>
            <Grid item className={clsx(classes.itemRootBorder, classes.indicatorCell)}>
              <NumberIndicator
                title="Exotico"
                value={getValue('exotic')}
                total={getComparisonValue('exotic')}
                rate={getRatio(getValue('exotic'), getComparisonValue('exotic'))}
                size="small"
                comparisonLabel={yearComparison[1]?.toString()}
              />
            </Grid>
            <Grid item className={classes.indicatorCell}>
              <NumberIndicator
                title="Riqueza"
                value={getValue('species')}
                total={getComparisonValue('species')}
                rate={getRatio(getValue('species'), getComparisonValue('species'))}
                size="small"
                comparisonLabel={yearComparison[1]?.toString()}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Box my={3} display="flex" flexDirection="column">
        <Box my={0.5} display="flex" alignItems="center">
          <Typography className={classes.subtitle} variant="body2" component="span">
            Especies por año
          </Typography>
          <Box flexGrow={1} />
          <Box display="flex" className={classes.group} p={0.5} ml={1}>
            <SpeciesTypeFilter speciesType={speciesByYearTypeFilter} setSpeciesType={setSpeciesByYearTypeFilter} />
          </Box>
        </Box>
        <Box justifyItems="center" p={3} className={classes.group} height={300}>
          <ChartWrapper type="line" options={{ responsive: true, maintainAspectRatio: false }} data={speciesChart} />
        </Box>
      </Box>
    </>
  );
};

SpeciesByYear.propTypes = {
  speciesByYear: PropTypes.shape({
    speciesIndicators: PropTypes.object.isRequired,
    speciesChart: PropTypes.object.isRequired,
  }).isRequired,
  setSpeciesByYear: PropTypes.func.isRequired,
};


export {
  SpeciesByYear,
};
